// BM-H398 - s2b_shares. Holdings feed the engine's BPR/APR estimate
// (`company_type`, `approx_value_gbp`, `held_2_years_plus`) and the share
// disposition clauses (C-10a, C-10b, C-10c). The follow-up questions are the
// fee earner's section 6.4 checklist, surfaced on the review summary.

import { MoneyInput, NumberInput, QuestionRow, RepeatList, SectionIntro, Select, TextArea, TextInput, YesNo, sectionProps, replaceAt, removeAt } from "./fields";
import PersonPicker, { PeopleList } from "./PersonPicker";
import { newId } from "./willsPayload";
import { CallbackHint, UnsupportedNotice } from "./removedOptions";

const COMPANY_TYPE_OPTIONS = [
  { value: "unquoted_trading", label: "Private trading company" },
  { value: "aim", label: "Shares quoted on AIM" },
  { value: "partnership", label: "Partnership" },
  { value: "llp", label: "LLP" },
  { value: "quoted", label: "Listed on a main stock exchange" },
  { value: "other", label: "Other" },
];

const HELD_OPTIONS = [
  { value: "yes", label: "Yes, two years or more" },
  { value: "no", label: "No, less than two years" },
  { value: "not_known", label: "Not known" },
];

export const DISPOSITION_OPTIONS = [
  { value: "via_residue", label: "Pass with the rest of my estate" },
  { value: "specific_gift_to", label: "Give to a named person" },
  { value: "business_succession_trust", label: "Hold on a business succession trust" },
  { value: "discuss", label: "I would like to discuss this" },
];

const YES_NO_NK = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "not_known", label: "Not known" },
];

const FOLLOW_UPS = [
  ["shareholders_agreement", "s2b_shares.follow_ups.shareholders_agreement", "Is there a shareholders' agreement?"],
  ["cross_option_agreement", "s2b_shares.follow_ups.cross_option_agreement", "Is there a cross option agreement?"],
  ["pre_emption_rights", "s2b_shares.follow_ups.pre_emption_rights", "Do the company's articles give other shareholders first refusal on death?"],
  ["key_person_insurance", "s2b_shares.follow_ups.key_person_insurance", "Is there key person or shareholder protection insurance?"],
];

// BM-H3381 - C-10b (business succession trust) could not be drafted at
// all: the client could choose the disposition but nothing captured who
// the trust is for or on what terms. Verified directly against the
// engine (wills-engine.cjs:682-684): RULES["C-10b"] only checks
// disposition === "business_succession_trust", so none of this blocks or
// changes clause selection - it exists purely to give the fee earner the
// actual terms to draft by hand (document assembly happens outside this
// codebase, per the established scope split). C-10c (trustee powers) is
// drafted separately and does not depend on any of this.
const BENEFICIARY_MODE_OPTIONS = [
  { value: "named", label: "Name the specific person or people" },
  { value: "class", label: "Describe a class (e.g. 'my children and their issue')" },
  { value: "same_as_residue", label: "Whoever I have named to receive the rest of my estate" },
];

// BM-H3636: the choice between a discretionary and a fixed interest business trust is no longer asked online
// (solicitor ruling, 5 Oct 2026). A fee earner settles the type. A saved answer is kept and flagged.

const FALLBACK_MODE_OPTIONS = [
  { value: "named", label: "Name a specific person or people" },
  { value: "class", label: "Describe a class" },
];

function BusinessSuccessionTrustTerms({ holding, onChange, payload, update, person, residueBeneficiaryIds, testid }) {
  const t = holding.trust_terms || {};
  const beneficiaries = t.beneficiaries || [];
  const fallbackBeneficiaries = t.fallback_beneficiaries || [];

  function setBeneficiaryMode(mode) {
    onChange({
      beneficiary_mode: mode,
      beneficiaries: mode === "same_as_residue" ? residueBeneficiaryIds.map((pid) => ({ person_id: pid })) : mode === "named" ? [] : undefined,
      beneficiary_class_description: mode === "class" ? "" : undefined,
    });
  }

  function setFallbackMode(mode) {
    onChange({
      fallback_mode: mode,
      fallback_beneficiaries: mode === "named" ? [] : undefined,
      fallback_class_description: mode === "class" ? "" : undefined,
    });
  }

  return (
    <div data-testid={testid}>
      <QuestionRow qid="s2b_shares.holdings.trust_terms.beneficiary_mode" label="Who should the trust be for?" testid={`${testid}-q-beneficiary-mode`}>
        <Select testid={`${testid}-beneficiary-mode`} value={t.beneficiary_mode} options={BENEFICIARY_MODE_OPTIONS} onChange={setBeneficiaryMode} />
      </QuestionRow>

      {t.beneficiary_mode === "named" && (
        <PeopleList
          payload={payload}
          update={update}
          qid="s2b_shares.holdings.trust_terms.beneficiaries"
          label="Trust beneficiary"
          items={beneficiaries}
          exclude={[person?.id]}
          onChange={(items) => onChange({ beneficiaries: items })}
          testid={`${testid}-beneficiaries`}
        />
      )}
      {t.beneficiary_mode === "class" && (
        <QuestionRow qid="s2b_shares.holdings.trust_terms.beneficiary_class_description" label="Describe the class" testid={`${testid}-q-beneficiary-class`}>
          <TextArea testid={`${testid}-beneficiary-class`} value={t.beneficiary_class_description} onChange={(v) => onChange({ beneficiary_class_description: v })} />
        </QuestionRow>
      )}
      {t.beneficiary_mode === "same_as_residue" && (
        <p data-testid={`${testid}-same-as-residue-note`} style={{ fontSize: 13, color: "#334155" }}>
          Uses the people you have already named to receive the rest of your estate.
        </p>
      )}

      <CallbackHint testid={`${testid}-trust-type-hint`}>Your fee earner will settle what kind of trust it is with you.</CallbackHint>

      <QuestionRow qid="s2b_shares.holdings.trust_terms.vesting_age" label="Should they only receive it once they reach a certain age? (leave blank if not)" testid={`${testid}-q-vesting-age`}>
        <NumberInput testid={`${testid}-vesting-age`} value={t.vesting_age} onChange={(v) => onChange({ vesting_age: v, fallback_mode: v ? t.fallback_mode : undefined, fallback_beneficiaries: v ? t.fallback_beneficiaries : undefined, fallback_class_description: v ? t.fallback_class_description : undefined })} />
      </QuestionRow>

      {t.vesting_age != null && t.vesting_age !== "" && (
        <>
          <QuestionRow qid="s2b_shares.holdings.trust_terms.fallback_mode" label="If nobody ever reaches that age, who should take it instead?" testid={`${testid}-q-fallback-mode`}>
            <Select testid={`${testid}-fallback-mode`} value={t.fallback_mode} options={FALLBACK_MODE_OPTIONS} onChange={setFallbackMode} />
          </QuestionRow>
          {t.fallback_mode === "named" && (
            <PeopleList
              payload={payload}
              update={update}
              qid="s2b_shares.holdings.trust_terms.fallback_beneficiaries"
              label="Fallback beneficiary"
              items={fallbackBeneficiaries}
              exclude={[person?.id]}
              onChange={(items) => onChange({ fallback_beneficiaries: items })}
              testid={`${testid}-fallback-beneficiaries`}
            />
          )}
          {t.fallback_mode === "class" && (
            <QuestionRow qid="s2b_shares.holdings.trust_terms.fallback_class_description" label="Describe the fallback class" testid={`${testid}-q-fallback-class`}>
              <TextArea testid={`${testid}-fallback-class`} value={t.fallback_class_description} onChange={(v) => onChange({ fallback_class_description: v })} />
            </QuestionRow>
          )}
        </>
      )}
    </div>
  );
}

export default function S2bShares({ section, setSection, payload, update, person, answers }) {
  const { s, set, nk, setNk } = sectionProps(section, setSection);
  const holdings = s.holdings || [];
  const ag = s.agricultural_property || {};
  const follow = s.follow_ups || {};
  const residueBeneficiaryIds = (answers?.s7_residue?.beneficiaries || []).map((b) => b.person_id).filter(Boolean);

  return (
    <div data-testid="section-s2b_shares">
      <UnsupportedNotice answers={answers || payload.submission.answers} section="s2b_shares" />
      <SectionIntro title="Business and farming interests">
        Any trading business or farming property needs its own tax analysis, so we ask about it here.
      </SectionIntro>

      <QuestionRow qid="s2b_shares.has_shares" label="Do you own shares in a company, or an interest in a partnership or business?">
        <YesNo testid="s2b-has-shares" value={s.has_shares} onChange={(v) => set({ has_shares: v, holdings: v ? holdings : [] })} />
      </QuestionRow>

      {s.has_shares === true && (
        <>
          <RepeatList
            testid="s2b-holdings"
            items={holdings}
            onAdd={() => set({ holdings: [...holdings, { id: newId("hld"), company_name: "", company_type: null, approx_value_gbp: null, held_2_years_plus: null, disposition: null, specific_gift_beneficiary_id: null }] })}
            onRemove={(i) => set({ holdings: removeAt(holdings, i) })}
            addLabel="Add a business interest"
            emptyText="No business interest added yet."
            render={(h, i) => {
              const edit = (patch) => set({ holdings: replaceAt(holdings, i, { ...h, ...patch }) });
              return (
                <>
                  <QuestionRow qid="s2b_shares.holdings.company_name" label="Business name" testid={`s2b-holding-${i}-q-name`}>
                    <TextInput testid={`s2b-holding-${i}-name`} value={h.company_name} onChange={(v) => edit({ company_name: v })} />
                  </QuestionRow>
                  <QuestionRow qid="s2b_shares.holdings.company_type" label="What kind of business is it?" testid={`s2b-holding-${i}-q-type`}>
                    <Select testid={`s2b-holding-${i}-type`} value={h.company_type} options={COMPANY_TYPE_OPTIONS} onChange={(v) => edit({ company_type: v })} />
                  </QuestionRow>
                  <QuestionRow qid="s2b_shares.holdings.approx_value_gbp" label="Rough value of your holding" evidence testid={`s2b-holding-${i}-q-value`}>
                    <MoneyInput testid={`s2b-holding-${i}-value`} value={h.approx_value_gbp} onChange={(v) => edit({ approx_value_gbp: v })} />
                  </QuestionRow>
                  <QuestionRow qid="s2b_shares.holdings.held_2_years_plus" label="Have you owned it for two years or more?" testid={`s2b-holding-${i}-q-held`}>
                    <Select testid={`s2b-holding-${i}-held`} value={h.held_2_years_plus} options={HELD_OPTIONS} onChange={(v) => edit({ held_2_years_plus: v })} />
                  </QuestionRow>
                  <QuestionRow qid="s2b_shares.holdings.disposition" label="What should happen to it?" testid={`s2b-holding-${i}-q-disposition`}>
                    <Select testid={`s2b-holding-${i}-disposition`} value={h.disposition} options={DISPOSITION_OPTIONS} onChange={(v) => edit({ disposition: v })} />
                  </QuestionRow>
                  {h.disposition === "specific_gift_to" && (
                    <PersonPicker payload={payload} update={update} qid="s2b_shares.holdings.specific_gift_beneficiary_id" label="Who should receive it?" value={h.specific_gift_beneficiary_id} exclude={[person?.id]} onChange={(pid) => edit({ specific_gift_beneficiary_id: pid })} testid={`s2b-holding-${i}-beneficiary`} />
                  )}
                  {h.disposition === "business_succession_trust" && (
                    <BusinessSuccessionTrustTerms
                      holding={h}
                      onChange={(patch) => edit({ trust_terms: { ...(h.trust_terms || {}), ...patch } })}
                      payload={payload}
                      update={update}
                      person={person}
                      residueBeneficiaryIds={residueBeneficiaryIds}
                      testid={`s2b-holding-${i}-trust`}
                    />
                  )}
                </>
              );
            }}
          />
          <div style={{ marginTop: 18 }} data-testid="s2b-follow-ups">
            {FOLLOW_UPS.map(([field, qid, label]) => (
              <QuestionRow key={field} qid={qid} label={label} evidence>
                <Select testid={`s2b-follow-up-${field}`} value={follow[field]} options={YES_NO_NK} onChange={(v) => set({ follow_ups: { ...follow, [field]: v } })} />
              </QuestionRow>
            ))}
          </div>
        </>
      )}

      <QuestionRow qid="s2b_shares.agricultural_property.owns" label="Do you own any farming land or buildings?" notKnown={nk("agricultural_property")} onNotKnown={(on) => setNk("agricultural_property", on)}>
        <YesNo testid="s2b-agricultural-owns" value={ag.owns ?? null} disabled={nk("agricultural_property")} onChange={(v) => set({ agricultural_property: { ...ag, owns: v, flagged_for_review: v === true } })} />
      </QuestionRow>
      {ag.owns === true && (
        <QuestionRow qid="s2b_shares.agricultural_property.approx_value_gbp" label="Rough value of the farming property" evidence>
          <MoneyInput testid="s2b-agricultural-value" value={ag.approx_value_gbp} onChange={(v) => set({ agricultural_property: { ...ag, approx_value_gbp: v } })} />
        </QuestionRow>
      )}
    </div>
  );
}
